import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { lazy, Suspense, useEffect } from 'react';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ProtectedRoute } from './components/ProtectedRoute';
import { AppErrorBoundary } from './components/AppErrorBoundary';
import { LoadingScreen } from './components/LoadingScreen';
import { applyTheme, readStoredAppearance, DEFAULT_APPEARANCE } from './lib/theme';
import { safeNextPath } from './lib/nextPath';
import { isGuest } from './lib/auth';

const LandingPage = lazy(() => import('./components/Landing/LandingPage').then(m => ({ default: m.LandingPage })));
const ResetPasswordPage = lazy(() => import('./components/Auth/ResetPasswordPage').then(m => ({ default: m.ResetPasswordPage })));
const AuthPage = lazy(() => import('./components/Auth/AuthPage').then(m => ({ default: m.AuthPage })));
const Dashboard = lazy(() => import('./components/Dashboard/Dashboard').then(m => ({ default: m.Dashboard })));
const PricingPage = lazy(() => import('./components/Subscription/PricingPage').then(m => ({ default: m.PricingPage })));
const OAuthCallback = lazy(() => import('./components/OAuthCallback').then(m => ({ default: m.OAuthCallback })));
const NotFoundPage = lazy(() => import('./components/NotFoundPage').then(m => ({ default: m.NotFoundPage })));

export function PublicRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <LoadingScreen />;
  // A guest is not a real account: they must still be able to reach /signup and /login.
  if (user && !isGuest(user)) return <Navigate to={safeNextPath(location.search)} replace />;
  return <>{children}</>;
}

/**
 * A password-reset link signs the reader in. If Supabase sends them anywhere
 * but /reset-password (for example to the Site URL), take them there so they
 * still choose a new password instead of landing on the dashboard.
 */
export function RecoveryRedirect() {
  const { passwordRecovery } = useAuth();
  const location = useLocation();
  if (!passwordRecovery || location.pathname === '/reset-password') return null;
  return <Navigate to="/reset-password" replace />;
}

function AppRoutes() {
  return (
    <Suspense fallback={<LoadingScreen />}>
      <RecoveryRedirect />
      <Routes>
        <Route path="/" element={<PublicRoute><LandingPage /></PublicRoute>} />
        <Route path="/login" element={<PublicRoute><AuthPage /></PublicRoute>} />
        <Route path="/signup" element={<PublicRoute><AuthPage /></PublicRoute>} />
        {/* Not a PublicRoute: the reset link has already signed the reader in. */}
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/dashboard" element={
          <ProtectedRoute><Dashboard /></ProtectedRoute>
        } />
        <Route path="/pricing" element={<PricingPage />} />
        <Route path="/oauth/callback" element={<OAuthCallback />} />
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </Suspense>
  );
}

export default function App() {
  useEffect(() => {
    applyTheme(readStoredAppearance() ?? DEFAULT_APPEARANCE);
  }, []);

  return (
    <AppErrorBoundary>
      <BrowserRouter>
        <AuthProvider>
          <AppRoutes />
        </AuthProvider>
      </BrowserRouter>
    </AppErrorBoundary>
  );
}
